<template>
  <div class="hall">
    <!--
      House style rule 1: every board opens with its place and its purse.
      The Hall used to open with a bare label and the compass; now it opens
      the way the Frontier and the Stewards do - where you are, and what you
      have left to spend today - with the compass tucked into the same line.
    -->
    <!-- No "The Hall · N workers left" heading: the role tab above says both
         (one-screen pass, 2026-09-26). -->


    <!-- A card that raises workers asks who gets them. One step, on its own. -->
    <template v-if="givingFrom">
      <!-- Split them however you like (Matt, playtest 7: "let them split them up"). -->
      <p class="counter-band">{{ givingLabel }} raises {{ gives }} {{ gives === 1 ? 'worker' : 'workers' }}. Share them out: {{ splitLeft }} left.</p>
      <ul class="split-list">
        <li v-for="role in ROLES" :key="role" class="split-row" :style="{ '--role': ROLE_INFO[role].colour }">
          <span class="split-row__name">{{ ROLE_INFO[role].label }}
            <small>{{ game.done[role] ? 'done for the day' : `${workersFor(role, hands[role])} now` }}</small>
          </span>
          <button class="split-row__step" :disabled="!split[role]" :aria-label="`One fewer to the ${ROLE_INFO[role].label}`" @click="nudge(role, -1)">−</button>
          <strong class="split-row__n">{{ split[role] || 0 }}</strong>
          <button class="split-row__step" :disabled="!splitLeft || game.done[role]" :aria-label="`One more to the ${ROLE_INFO[role].label}`" @click="nudge(role, 1)">+</button>
        </li>
      </ul>
      <button class="primary" :disabled="splitLeft > 0" @click="finishGiving">{{ splitLeft ? `Place ${splitLeft} more` : 'Send them' }}</button>
      <button class="secondary" @click="givingFrom = null">Back</button>
    </template>

    <!-- The Charter names its village (playtest 13: two Charters, and the
         play picked one on its own - "it would've been nice if we could've
         picked"). -->
    <template v-else-if="charterFrom">
      <p class="counter-band">The Charter: which village becomes a town?</p>
      <ul class="split-list">
        <li v-for="v in charterable" :key="v.id" class="split-row">
          <span class="split-row__name">{{ v.name }} <small>size {{ v.size }} · {{ v.makes }}</small></span>
          <button class="primary split-row__pick" @click="finishCharter(v.id)">Charter {{ v.name }}</button>
        </li>
      </ul>
      <p class="phone__note">A town fills a standing order (food, goods or coin), units can muster there, it grows to 3, and it eats 1 food a night.</p>
      <button class="secondary" @click="charterFrom = null">Back</button>
    </template>

    <template v-else>
      <!--
        The shelf: one full-width bar, collapsed - the label, the three
        ideas as mini cards, and the buys you have. Matt: "it feels like the
        available cards are too prominent... maybe we collapse them, make
        them take less space, but still be clear that you get to buy a
        card." Tap it and the cards open under it at their proper size.
      -->
      <!--
        The shelf and the compass on ONE ROW (Matt, 2026-09-22): the square,
        exactly the header's 30px, just right of the bar with a little
        padding; the bar takes the rest. Tapping the square opens the
        governments under the row, full width.
      -->
      <div class="hall__row">
      <section class="shelf" data-tut="hall-shelf" :class="{ 'shelf--open': pane === 'cards' }">
        <button class="shelf__bar" @click="toggle('cards')">
          <span class="shelf__label">Buy a card</span>
          <span v-if="pane !== 'cards'" class="shelf__minis" aria-hidden="true">
            <span v-for="c in marketCards" :key="c.id" class="shelf__mini">
              <!-- eslint-disable-next-line vue/no-v-html -- constant art from cardArt.js -->
              <svg viewBox="0 0 48 48" v-html="miniArt(c)" />
            </span>
          </span>
          <span class="zone__count" :class="{ 'zone__count--spent': !buysLeft, 'shelf__count': pane === 'cards' }">{{ buysLeft }} buy{{ buysLeft === 1 ? '' : 's' }}</span>
          <span class="shelf__chevron" aria-hidden="true">{{ pane === 'cards' ? '\u2303' : '\u2304' }}</span>
        </button>
        <template v-if="pane === 'cards'">
          <!-- THE SHELF IS THE COUNCIL'S (playtest 13): each card is one
               counselor's idea, and says whose. The fourth, the burden, PAYS
               you to take it (Nick's idea). -->
          <!-- A card is BOUGHT ONCE and played free (playtest 14): the tag
               under it is the whole price. -->
          <ul class="hall__market" :class="{ 'hall__market--four': burdenCard }">
            <li v-for="c in marketCards" :key="c.id">
              <small class="shelf__by" :title="c.by ? `${c.by}'s idea` : null">{{ c.by || '\u00a0' }}</small>
              <button class="hcard-btn" :disabled="!canAct || !c.check.ok" @click="act('buy', { card: c.id })">
                <HallCard :card="c" :off="!c.check.ok" :ready="c.check.ok" :liked="c.liked" small />
                <span class="buy-tag" :class="{ 'buy-tag--off': !c.check.ok }">
                  <span class="buy-tag__hole" aria-hidden="true" />
                  <ActionCost :cost="c.price" />
                </span>
              </button>
              <!-- The note under the cards says why when there is no buy; a
                   card says why only when it is the card's own price. -->
              <span v-if="!c.check.ok && buysLeft" class="hcard-btn__why">{{ c.check.why }}</span>
            </li>
            <!-- THE BURDEN IS A CARD LIKE THE OTHERS (playtest 14, Matt: "the
                 UI for it is terrible... it needs to look like a card"). Its
                 tag runs the other way: what it PAYS you. -->
            <li v-if="burdenCard" class="hall__market-burden">
              <small class="shelf__by shelf__by--pays">pays you</small>
              <button class="hcard-btn" :disabled="!canAct || !burdenCard.check.ok" :title="burdenCard.check.why" @click="act('burden')">
                <HallCard :card="burdenCard" :off="!burdenCard.check.ok" :ready="burdenCard.check.ok" small />
                <span class="buy-tag buy-tag--pays" :class="{ 'buy-tag--off': !burdenCard.check.ok }">
                  <span class="buy-tag__hole" aria-hidden="true" />
                  <Pip v-for="g in burdenCard.bountyPips" :key="g.kind" :kind="g.kind" :n="g.n" signed />
                </span>
              </button>
            </li>
            <li v-if="!marketCards.length" class="phone__note">Nothing on offer.</li>
          </ul>
          <p v-if="burdenCard" class="shelf__burden-note"><b>{{ burdenCard.name }}</b> pays you now, no buy needed. Then it sits in your deck as a grievance: play it to settle it (it gives nothing), or trash it for a coin. In hand at night, it costs 1 happiness.</p>
          <!-- What the council would bring if the town could take it up: the
               greyed next thing, with who to ask (Matt, playtest 13: "show
               people on their board the next thing they could do if they
               had a specific technology"). -->
          <ul v-if="comingIdeas.length" class="shelf__coming">
            <li v-for="c in comingIdeas" :key="c.id"><b>{{ c.name }}</b> <span>from {{ c.by }} · {{ c.need }}</span></li>
          </ul>
        </template>
      </section>
      <!-- THE COMPASS WAITS (playtest 14, Matt: hide it until it matters).
           Nothing here until the town is one push from a government; then
           the square and one line saying what that push would do. -->
      <button v-if="compassShown" class="gov-square" data-tut="hall-compass" :class="{ 'gov-square--on': pane === 'leaning' }" :aria-label="pane === 'leaning' ? 'Hide the governments' : `Show the governments (${governmentShort})`" :title="governmentShort" @click="toggle('leaning')">
        <Compass :civ="civ" bare />
      </button>
      </div>
      <p v-if="nearGovernment && pane !== 'leaning'" class="hall__gov-near">{{ nearGovernment }}</p>

      <section v-if="pane === 'leaning'" class="pane hall__leaning">
        <div class="hall__leaning-top">
          <Compass :civ="civ" />
          <div class="hall__leaning-now">
            <strong>{{ governmentName }}</strong>
            <span>{{ here }}</span>
            <span>Every card moves the dot a step. Reach 2 on both axes and the town takes that government, and keeps it until the dot reaches another.</span>
            <span>What moves it: a card says its push when you pick it up; Trash is always +1 Novelty.</span>
          </div>
        </div>
        <ul class="govs">
          <li v-for="g in governments" :key="g.id" class="gov" :class="{ 'gov--on': g.now }">
            <span class="gov__head">
              <strong>{{ g.name }}</strong>
              <span class="gov__where">{{ g.where }}</span>
              <span class="gov__steps" :class="{ 'gov__steps--on': g.now }">{{ g.now ? 'now' : `${g.steps} step${g.steps === 1 ? '' : 's'}` }}</span>
            </span>
            <span class="gov__what">{{ g.short }}</span>
          </li>
        </ul>
      </section>

      <!--
        The council IS the board. Every card you play goes onto a seat, and a
        seat takes one card a day. Matt: "you assign cards to counselors and
        the same card can work differently depending on the counselor you play
        it on... the choice the player is making is which card goes with which
        councilor."
      -->
      <!-- With a card up, the heading says the next move (the learner,
           2026-10-12: three runs in three tapped the lifted card again). -->
      <h4 class="zone" :class="{ 'zone--loud': chosen }">
        <span class="zone__label">{{ chosen ? 'Now tap a counselor' : 'The council' }}</span>
        <!-- Who could sit next, and what would bring them (Matt, playtest 13:
             "a pool from which the election choice gets pulled... they could
             say to the frontier, hey, if you can find the marsh"). -->
        <button v-if="!chosen" class="linkish zone__link" :class="{ 'zone__link--on': pane === 'pool' }" @click="toggle('pool')">{{ pane === 'pool' ? 'hide the pool' : 'who could sit' }}</button>
        <span class="zone__count" :class="{ 'zone__count--spent': !openSeats }">{{ openSeats }} of {{ council.length }} free</span>
      </h4>
      <section v-if="pane === 'pool'" class="pane pool">
        <p class="pool__lead">When a chair empties, two are drawn from those who are <b>ready</b>. The greyed ones need something first - ask.</p>
        <ul class="pool__list">
          <li v-for="c in pool" :key="c.id" class="pool__one" :class="[`pool__one--${c.status}`]" :style="c.askRole ? { '--role': ROLE_INFO[c.askRole].colour } : null">
            <span class="pool__face" aria-hidden="true">{{ c.name.replace(/^The /, '').charAt(0) }}</span>
            <span class="pool__who"><strong>{{ c.name.replace(/^The /, '') }}</strong> <small>{{ c.job }}</small></span>
            <span class="pool__state">
              <template v-if="c.status === 'offered'">put forward</template>
              <template v-else-if="c.status === 'ready'">ready</template>
              <template v-else-if="c.status === 'resting'">back in {{ c.back }}</template>
              <template v-else-if="c.status === 'moot'">moot only</template>
              <template v-else>not yet</template>
            </span>
            <span v-if="c.status === 'locked'" class="pool__ask" :class="{ 'pool__ask--role': c.askRole }">{{ c.askRole ? 'Ask ' : '' }}{{ c.ask || c.emerges }}</span>
            <span class="pool__via">{{ c.likes }}</span>
          </li>
        </ul>
      </section>

      <div class="pop-wrap">
      <ul class="seats" data-tut="hall-council">
        <li v-for="s in seats" :key="s.id">
          <button
            class="seat"
            :class="[`seat--${s.state}`, { 'seat--target': chosen && s.state === 'free', 'seat--likes': chosen && s.state === 'free' && s.likesChosen, 'seat--open': openSeat === s.id }]"
            :disabled="chosen ? !s.place || !s.place.check.ok : false"
            @click="tapSeat(s)"
          >
            <span class="seat__face" aria-hidden="true">{{ s.initial }}</span>
            <span class="seat__name">{{ s.short }}</span>

            <template v-if="chosen && s.place">
              <!-- A play is free (playtest 14): the chair shows only what
                   comes back, and says "likes it" when its bonus is in. -->
              <span v-if="s.likesChosen" class="seat__likes-it">likes it</span>
              <span class="seat__pips">
                <Pip v-for="g in s.place.gainPips" :key="g.kind" :kind="g.kind" :n="g.n" signed />
                <span v-if="!s.place.gainPips.length && !s.place.leans.length && !s.place.note" class="seat__nothing">—</span>
              </span>
              <span v-if="s.place.leans.length" class="seat__lean">{{ s.place.leans.join(' · ') }}</span>
              <span v-if="s.place.note" class="seat__note">{{ s.place.note }}</span>
              <span v-if="!s.place.check.ok" class="seat__why">{{ s.place.check.why }}</span>
            </template>
            <template v-else-if="s.state === 'taken'">
              <span class="seat__note">took {{ s.tookName }} today</span>
            </template>
            <template v-else>
              <!-- The one thing to know about a chair, printed on it. -->
              <span class="seat__likes">{{ s.likes }}</span>
            </template>
          </button>
        </li>
      </ul>
      <!--
        A POPOVER, not a panel: as big as it needs to be, over the cards,
        with an arrow at the counselor it is about. Matt, 2026-09-20: "what
        if instead of this, we had those pop up, like as a pop up on top of
        the other content... it can just be as big as it needs to be and we
        just dismiss it when we need to dismiss it."
      -->
      <!-- It hangs under the row its counselor is in: one put forward for an
           empty chair sits in the door row below, and the arrow used to point
           at whoever sat in the middle of the council (playtest z8PwA). -->
      <div id="hall-pop-council" />
      <Teleport v-if="openSeatDetail" defer :to="popAtDoor ? '#hall-pop-door' : '#hall-pop-council'">
      <div class="pop" :style="{ '--pop-x': popX }" @click.stop>
        <span class="pop__arrow" aria-hidden="true" />
        <div class="pop__head">
          <strong>{{ openSeatDetail.name }}</strong>
          <span class="pop__job">{{ openSeatDetail.job }}</span>
        </div>
        <span class="pop__job">{{ openSeatDetail.job }}</span>
        <span class="pop__via"><strong>Likes</strong> {{ openSeatDetail.likes }}</span>
        <span class="pop__age">{{ openSeatDetail.age }}</span>
        <div class="pop__feet">
          <button class="pop__close" @click="openSeat = null">Close</button>
          <!--
            The one destructive thing on this board, kept away from the
            corner a close button lives in: Matt tapped "dismiss" meaning
            "dismiss this panel" and sent a counselor home.
          -->
          <button
            v-if="canAct && openSeatDetail.action"
            class="pop__act"
            :class="{ 'pop__act--danger': openSeatDetail.action.id === 'dismiss' }"
            :disabled="!openSeatDetail.action.check.ok"
            :title="openSeatDetail.action.check.why"
            @click="act(openSeatDetail.action.id, { counselor: openSeatDetail.id })"
          >{{ openSeatDetail.action.label }}</button>
        </div>
      </div>
      </Teleport>
    </div>

      <!--
        The slot under the council is always there, so picking a card up
        never shoves the hand down. Face up/face down lived here until
        playtest 7 retired face-down plays; now it offers to trash the card.
      -->
      <!-- Both always laid out, hidden until a card is up: the slot is the
           same height with a card or without, so lifting one never moves
           the hand (playtest, 2026-10-15: "it's annoying when it's jumpy"). -->
      <div class="mode-slot" :class="{ 'mode-slot--empty': !chosen }">
        <!-- What the lifted card SAYS (playtest 11, Nick with a full hand:
             "I don't know what any of these mean"): its own words, once. -->
        <p class="held-text"><template v-if="chosen"><strong>{{ chosen.name }}.</strong> {{ chosen.text }}</template></p>
        <button class="trash-btn" data-tut="hall-trash" :disabled="!chosen || !trashCheck.ok" :title="trashCheck.why" :tabindex="chosen ? 0 : -1" @click="chosen && act('cull', { card: chosen.id })">
          Or trash {{ chosen ? chosen.name : 'it' }} instead
          <small>once a day · 1 coin · gone for good · +1 Novelty</small>
        </button>
      </div>

      <!-- Standing laws, where the Hall can see them (Matt, playtest 13,
           twice: "the hall should be able to see what laws are in place and
           what they do"). One line; they used to live only behind My deck. -->
      <p v-if="lawsInForce.length" class="laws-row">
        <b>Laws:</b>
        <span v-for="l in lawsInForce" :key="l.id" class="laws-row__law"><strong>{{ l.name }}</strong> · {{ l.what }}</span>
      </p>

      <!--
        An empty chair: the two put forward for it, dashed, under the
        council. Tap one to read them; the popover's button gives them the
        chair. (Matt, playtest 6: counselors wear out on a randomised
        timer, and when one goes you pick from two.)
      -->
      <template v-if="offered.length">
        <h4 class="zone">
          <span class="zone__label">{{ hall.moot ? 'A moot for the empty chair' : 'A chair is empty' }}</span>
          <span class="zone__count">{{ hall.moot ? 'back one · 4+ on a die' : 'pick one' }}</span>
        </h4>
        <div class="pop-wrap">
        <ul class="seats seats--door">
          <li v-for="w in offered" :key="w.id">
            <button class="seat seat--waiting" @click="openSeat = openSeat === w.id ? null : w.id">
              <span class="seat__face" aria-hidden="true">{{ w.initial }}</span>
              <span class="seat__name">{{ w.short }}</span>
              <span class="seat__job">{{ w.job }}</span>
              <span class="seat__note">{{ hall.moot ? 'stands at the moot' : 'put forward' }}</span>
            </button>
          </li>
        </ul>
        <div id="hall-pop-door" />
        </div>
      </template>
      <ul v-if="waiting.length" class="seats seats--door">
        <li v-for="w in waiting" :key="w.id">
          <button class="seat seat--waiting" @click="openSeat = openSeat === w.id ? null : w.id">
            <span class="seat__face" aria-hidden="true">{{ w.initial }}</span>
            <span class="seat__name">{{ w.short }}</span>
            <span class="seat__note">at the door</span>
          </button>
        </li>
      </ul>

      <div class="hall__table">
        <CardStack :count="hall.deck.length" label="deck" @open="showDeck = 'deck'" />
        <div class="hall__fan" data-tut="hall-hand" :style="{ '--fan-h': `${fanHeight}px` }">
          <button
            v-for="(c, i) in handCards"
            :key="`${c.id}-${i}`"
            class="fan__card"
            :class="{ 'fan__card--up': chosenIndex === i }"
            :style="fanStyle(i)"
            :disabled="readonly"
            @click="tapHand(i)"
          >
            <!-- A five-card hand draws narrower cards, so the type shrinks with them. -->
            <HallCard :card="c" :ready="chosenIndex === i" :small="cardWidth() < 100" :liked="c.liked" />
            <!-- In your hand, and what to do with it (the learner: a lifted
                 card was tapped again and again, which puts it back). -->
            <span v-if="chosenIndex === i" class="fan__held" aria-hidden="true">In hand ↑ tap a counselor</span>
          </button>
          <!-- The hand is dealt when the day starts, two cards for every
               worker the split gives the Hall (Matt, 2026-10-14). -->
          <p v-if="!handCards.length" class="phone__note">{{ game.meta.phase === 'dawn' ? 'Your hand is dealt when the day starts: 2 cards for each worker the Hall is given.' : 'Nothing in hand.' }}</p>
        </div>
        <CardStack :count="hall.discard.length" label="discard" @open="showDeck = 'discard'" />
      </div>

      <!--
        The shared action. Every other board lists it; this one is hand-
        built and never did, so at the table the Hall had no way to put a
        day into the great work: "that button never seemed to show up."
      -->
      <template v-if="greatWork">
        <h4 class="zone">
          <span class="zone__label">The great work</span>
        </h4>
        <ul class="hall__actions hall__actions--fit">
          <li>
            <button class="tile" :disabled="!canAct || !greatWork.check.ok" @click="act('build-monument')">
              <span class="tile__head">
                <!-- eslint-disable-next-line vue/no-v-html -- constant art from buildingArt.js -->
                <svg class="tile__icon" viewBox="0 0 24 24" aria-hidden="true" v-html="greatWork.art" />
                <span class="tile__name">{{ greatWork.label }}</span>
              </span>
              <ActionCost :cost="greatWork.cost" />
              <span class="tile__gain">{{ greatWork.gain }}</span>
              <span v-if="!greatWork.check.ok && greatWork.check.kind !== 'cost'" class="action__why">{{ greatWork.check.why }}</span>
            </button>
          </li>
        </ul>
      </template>

      <!-- The Bronze Age's verb for the Hall: a treaty with a friendly people
           (only when one can be signed, so it never takes room before). -->
      <ul v-if="treaties.length" class="hall__actions hall__actions--fit">
        <li v-for="t in treaties" :key="t.id">
          <button class="tile" :disabled="!canAct || !t.check.ok" @click="act('treaty', { rival: t.id })">
            <span class="tile__head"><span class="tile__name">Treaty: {{ t.name }}</span></span>
            <ActionCost :cost="{ coin: 4 }" />
            <span class="tile__gain">allies: carts twice as full, no raids</span>
          </button>
        </li>
      </ul>
    </template>

    <DeckDialog v-if="showDeck" :civ="civ" :open="showDeck" @close="showDeck = false" />
  </div>
</template>

<script>
import {
  ROLES, ROLE_INFO, workersFor,
} from '@/engine/constants';
import {
  checkAction, cardGives, cardPush, cardDraws, sendsToBand, bonusesFor, MONUMENT_ACTION, resolveCost, resolveLabel,
} from '@/engine/roleActions';
import { civFor, handsOf } from '@/engine/game';
import {
  HALL_CARDS, hallOf, governmentOf, GOVERNMENTS, cardPrice, compassOf,
  GOVERNMENT_CORNERS, stepsTo,
  COUNSELORS, COUNCIL_SEATS, councilOf, waitingOf, isOld, likesCard, counselorsFor, seatUsed, buysOf, offerOf, termLeft,
  candidatePool, offeredBy, poolFor,
} from '@/engine/hall';
import { villagesOf, villageYield } from '@/engine/roleActions';
import { VILLAGE } from '@/engine/constants';
import { TECHS } from '@/engine/cards';
import { lawText } from './hallFaces';
import Compass from './Compass.vue';
import HallCard from './HallCard.vue';
import CardStack from './CardStack.vue';
import DeckDialog from './DeckDialog.vue';
import ActionCost from './ActionCost.vue';
import Pip from './Pip.vue';
import { faceFor, leanList } from './hallFaces';
import { artFor } from './cardArt';
import { artForAction } from './buildingArt';
import { SPENDABLE } from './resourceArt';

// The Hall's board: a shelf of cards to buy, a council of three seats, and
// your hand. Every card you play goes onto a seat - face up for the card,
// face down for the counselor - and each seat takes one a day. That is the
// whole turn, and it is a placement puzzle rather than a menu.
const GAIN_ORDER = [...SPENDABLE, 'cards', 'population', 'happiness', 'threat'];
const pipsOf = (gain = {}) => GAIN_ORDER.filter((k) => gain[k]).map((k) => ({ kind: k, n: gain[k] }));
// What the fan has to live inside, between the deck and the discard piles.
const FAN_WIDTH = 272;
const short = (name) => String(name || '').replace(/^The /, '');

export default {
  name: 'HallBoard',
  components: {
    Compass, HallCard, DeckDialog, CardStack, ActionCost, Pip,
  },
  props: {
    game: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
    preview: { type: Boolean, default: false },
  },
  emits: ['act'],
  data() {
    return {
      // Which pane is open under the row: 'cards', 'leaning', or none.
      pane: null,
      showDeck: false,
      openSeat: null,
      chosenIndex: null,
      split: {},
      givingFrom: null,
      givingSeat: null,
      givingGives: 0,
      charterFrom: null,
      charterSeat: null,
      HALL_CARDS,
      ROLES,
      ROLE_INFO,
      COUNCIL_SEATS,
      // A tablet has room for real-sized cards: the whole fan scales up
      // (iPad check, 2026-10-11 - the phone's fan sat tiny in the middle).
      fanScale: 1,
    };
  },
  mounted() {
    this.tabletQuery = window.matchMedia?.('(min-width: 760px)');
    this.onTablet = () => { this.fanScale = this.tabletQuery?.matches ? 1.6 : 1; };
    this.onTablet();
    this.tabletQuery?.addEventListener?.('change', this.onTablet);
  },
  beforeUnmount() {
    this.tabletQuery?.removeEventListener?.('change', this.onTablet);
  },
  computed: {
    civ() { return civFor(this.game, 'culture'); },
    hall() { return hallOf(this.civ) || { deck: [], hand: [], discard: [], market: [], laws: [], used: {} }; },
    hands() { return handsOf(this.game); },
    canAct() { return !this.readonly && !this.preview; },
    deckTotal() { return this.hall.deck.length + this.hall.hand.length + this.hall.discard.length; },
    buysLeft() { return buysOf(this.civ); },
    governmentName() {
      const g = governmentOf(this.civ);
      return g ? GOVERNMENTS[g].name : 'No government yet';
    },
    governmentShort() {
      const g = governmentOf(this.civ);
      return g ? GOVERNMENTS[g].name.replace(/^Council of /, '') : 'no government';
    },
    // A stage of the great work, once there is one to raise.
    greatWork() {
      if (!this.civ.monument?.revealed) return null;
      // The Hall's workers are its cards, so it has none to lay a stone with
      // unless a Levy handed it some; a tile it can never press is noise.
      if (this.game.meta?.phase === 'day' && !this.hands.culture) return null;
      const a = MONUMENT_ACTION;
      return {
        label: resolveLabel(a, this.civ),
        gain: a.gain(this.civ),
        cost: resolveCost(a, this.civ, 1, {}, this.game),
        art: artForAction(a.id),
        check: checkAction(this.game, 'culture', a.id),
      };
    },
    here() {
      const { x, y } = compassOf(this.civ);
      const bits = [
        x ? `${Math.abs(x)} ${x < 0 ? 'Tradition' : 'Novelty'}` : '',
        y ? `${Math.abs(y)} ${y < 0 ? 'Order' : 'Liberty'}` : '',
      ].filter(Boolean);
      return bits.length ? bits.join(', ') : 'In the middle';
    },
    // The compass shows once the town is one push from a government, or
    // sits in one (playtest 14: "hide it until it matters").
    compassShown() {
      return Boolean(governmentOf(this.civ)) || this.governments.some((g) => g.steps <= 1);
    },
    nearGovernment() {
      if (governmentOf(this.civ)) return '';
      const g = this.governments.find((x) => x.steps <= 1);
      if (!g) return '';
      return `One more push toward ${g.where} and the town becomes a ${g.name.replace(/^Council of /, '')}: ${g.short}`;
    },
    governments() {
      const now = governmentOf(this.civ);
      return Object.keys(GOVERNMENT_CORNERS).map((id) => ({
        id,
        name: GOVERNMENTS[id].name,
        where: GOVERNMENTS[id].where,
        short: GOVERNMENTS[id].short,
        steps: stepsTo(this.civ, id),
        now: id === now,
      })).sort((a, b) => (b.now - a.now) || (a.steps - b.steps));
    },
    gives() { return this.givingFrom ? this.givingGives : 0; },
    splitLeft() { return Math.max(0, this.gives - Object.values(this.split).reduce((a, b) => a + (b || 0), 0)); },
    treaties() {
      if ((this.civ.age || 0) < 1) return [];
      return (this.civ.rivals || []).filter((r) => r.met && r.stance === 'friendly' && !r.allied)
        .map((r) => ({ id: r.id, name: r.name, check: checkAction(this.game, 'culture', 'treaty', { rival: r.id }) }));
    },
    trashCheck() { return this.chosen ? checkAction(this.game, 'culture', 'cull', { card: this.chosen.id }) : { ok: false }; },
    givingLabel() { return this.givingFrom ? HALL_CARDS[this.givingFrom].name : ''; },

    council() { return councilOf(this.civ); },
    openSeats() { return this.council.filter((c) => !seatUsed(this.civ, c.id)).length },
    // The middle of the seat the popover is about. The row is a grid of
    // COUNCIL_SEATS columns whether or not every chair is filled, so two
    // counselors sit in the first two thirds, not in the middle of halves.
    // A counselor still at the door gets the middle.
    popAtDoor() { return this.offered.some((w) => w.id === this.openSeat); },
    popX() {
      const row = this.popAtDoor ? this.offered : this.seats;
      const i = row.findIndex((s) => s.id === this.openSeat);
      return `${i < 0 ? 50 : ((i + 0.5) / COUNCIL_SEATS) * 100}%`;
    },

    handCards() {
      return this.hall.hand.map((id) => ({
        ...faceFor(this.civ, id),
        liked: counselorsFor(this.civ, id).some((who) => !seatUsed(this.civ, who)),
        check: checkAction(this.game, 'culture', 'play', { card: id, counselor: this.council[0]?.id }),
      }));
    },
    chosen() { return this.chosenIndex === null ? null : this.handCards[this.chosenIndex] || null; },

    // Every seat, and - when a card is in your fingers - exactly what that
    // seat would do with it, the way it is laid down.
    seats() {
      const round = this.game.meta.round;
      return this.council.map((c) => {
        const person = COUNSELORS[c.id];
        const used = seatUsed(this.civ, c.id);
        return {
          id: c.id,
          state: used ? 'taken' : 'free',
          name: person.name,
          short: short(person.name),
          initial: short(person.name).slice(0, 1).toUpperCase(),
          job: person.job,
          likes: person.likes?.text || '',
          likesChosen: Boolean(this.chosen) && likesCard(c.id, this.chosen.id),
          tookName: HALL_CARDS[this.hall.used?.[c.id]]?.name || 'a card',
          old: isOld(this.civ, c.id, round),
          place: this.chosen && !used ? this.placement(c.id) : null,
        };
      });
    },
    waiting() {
      return waitingOf(this.civ).map((id) => ({
        id,
        short: short(COUNSELORS[id].name),
        initial: short(COUNSELORS[id].name).slice(0, 1).toUpperCase(),
      }));
    },
    offered() {
      if (this.council.length >= COUNCIL_SEATS) return [];
      return offerOf(this.civ).map((id) => ({
        id,
        short: short(COUNSELORS[id].name),
        job: COUNSELORS[id].job,
        initial: short(COUNSELORS[id].name).slice(0, 1).toUpperCase(),
      }));
    },
    openSeatDetail() {
      const id = this.openSeat;
      if (!id || !COUNSELORS[id]) return null;
      const person = COUNSELORS[id];
      const onCouncil = this.council.some((c) => c.id === id);
      const c = this.council.find((x) => x.id === id);
      return {
        id,
        name: person.name,
        job: person.job,
        saying: person.text,
        likes: person.likes?.text || '',
        age: onCouncil
          ? [
            isOld(this.civ, id, this.game.meta.round) ? 'Old: still takes cards, but no longer pulls the compass.' : `${Math.max(0, this.game.meta.round - c.since)} months in the seat.`,
            termLeft(this.civ, id, this.game.meta.round) !== null ? `Retires in ${termLeft(this.civ, id, this.game.meta.round)} ${termLeft(this.civ, id, this.game.meta.round) === 1 ? 'month' : 'months'}.` : '',
          ].filter(Boolean).join(' ')
          : (offerOf(this.civ).includes(id) ? 'Put forward for the empty chair.' : 'Waiting for a chair.'),
        action: onCouncil
          ? { id: 'dismiss', label: 'Send them home', check: checkAction(this.game, 'culture', 'dismiss', { counselor: id }) }
          : (offerOf(this.civ).includes(id)
            ? { id: 'choose', label: this.hall.moot ? 'Back them (4+ seats them)' : 'Give them the chair', check: checkAction(this.game, 'culture', 'choose', { counselor: id }) }
            : { id: 'seat', label: 'Give them a chair', check: checkAction(this.game, 'culture', 'seat', { counselor: id }) }),
      };
    },

    marketCards() {
      return this.hall.market.map((id) => {
        const who = offeredBy(this.hall, id);
        return {
          ...faceFor(this.civ, id),
          price: cardPrice(id),
          by: who ? COUNSELORS[who].name.replace(/^The /, 'the ') : '',
          liked: counselorsFor(this.civ, id).length > 0,
          check: checkAction(this.game, 'culture', 'buy', { card: id }),
        };
      });
    },
    // The fourth card: a grievance with a bounty, no buy needed.
    burdenCard() {
      const id = this.hall.burden;
      if (!id || !HALL_CARDS[id]) return null;
      return {
        ...faceFor(this.civ, id),
        bountyPips: pipsOf(HALL_CARDS[id].bounty || {}),
        check: checkAction(this.game, 'culture', 'burden'),
      };
    },
    // Each seated counselor's ideas the town cannot take up yet, and why.
    comingIdeas() {
      const pool = new Set(poolFor(this.civ));
      const out = [];
      const seen = new Set();
      councilOf(this.civ).forEach((c) => {
        (COUNSELORS[c.id].offers || []).forEach((id) => {
          const card = HALL_CARDS[id];
          if (!card || pool.has(id) || seen.has(id)) return;
          seen.add(id);
          const need = card.needsTech ? `needs ${TECHS[card.needsTech]?.name || card.needsTech} - ask the Scholars`
            : card.needsBranch ? `needs the ${card.needsBranch === 'arts' ? 'Arts (a town at 8 happiness)' : card.needsBranch === 'statecraft' ? 'Statecraft branch (7 people)' : `${card.needsBranch} branch`}`
              : card.needsRival ? 'needs a people met - ask the Frontier' : 'not yet';
          out.push({ id, name: card.name, by: COUNSELORS[c.id].name.replace(/^The /, 'the '), need });
        });
      });
      return out.slice(0, 4);
    },
    lawsInForce() { return (this.hall.laws || []).map((id) => ({ id, name: HALL_CARDS[id]?.name || id, what: lawText(id) })); },
    pool() {
      const order = { offered: 0, ready: 1, locked: 2, resting: 3, moot: 4 };
      return candidatePool(this.civ, this.game.meta?.round || 1).sort((a, b) => order[a.status] - order[b.status]);
    },
    // Villages the lifted Charter could name.
    charterable() {
      return villagesOf(this.civ).filter((v) => !v.chartered && v.size >= VILLAGE.charterAtSize).map((v) => {
        const w = villageYield(this.civ, v);
        const k = Object.keys(w)[0];
        return { id: v.id, name: v.name, size: v.size, makes: k ? `makes ${k}` : 'makes nothing yet' };
      });
    },
    miniArt() { return (c) => artFor(c.id, c.art); },
    fanHeight() { return this.handCards.length ? Math.round((this.handCards.length >= 5 ? 152 : 176) * this.fanScale) : 0; },
  },
  methods: {
    toggle(which) { this.pane = this.pane === which ? null : which; },
    // Tokens back to people: the Stewards place half-days, but a tab that
    // counts heads must count heads.
    workersFor,
    // What this seat does with the card in your fingers, laid down the way
    // the switch says.
    placement(counselor) {
      const card = this.chosen;
      const check = checkAction(this.game, 'culture', 'play', { card: card.id, counselor });
      const face = faceFor(this.civ, card.id, counselor);
      const draw = cardDraws(this.civ, card.id, counselor);
      const gives = cardGives(this.civ, card.id, counselor);
      const bonuses = bonusesFor(this.civ, card.id, counselor);
      return {
        check,
        gainPips: pipsOf({ ...face.gain, ...(draw ? { cards: draw } : {}) }),
        leans: leanList(cardPush(card.id, counselor, this.civ, this.game.meta.round)),
        note: [
          gives && sendsToBand(this.civ, card.id, counselor) ? 'as orders to the Frontier' : (gives ? 'to a role you name' : ''),
          HALL_CARDS[card.id].law ? 'becomes a law' : '',
          HALL_CARDS[card.id].grievance ? 'settled' : '',
          HALL_CARDS[card.id].buys ? '+1 buy' : '',
          bonuses.some((b) => b.pair) ? 'pairs up' : '',
          bonuses.some((b) => b.reveal) ? 'reads out a nature' : '',
          bonuses.some((b) => b.stone) ? 'great work −1 goods' : '',
        ].filter(Boolean).join(' · '),
      };
    },
    // Five cards have to fit between the deck and the discard, so the cards
    // themselves shrink as the hand grows rather than spilling over the piles.
    cardWidth() {
      const n = this.handCards.length;
      const base = n >= 5 ? 92 : n === 4 ? 100 : 110;
      return Math.round(base * this.fanScale);
    },
    fanStyle(i) {
      const n = this.handCards.length;
      const up = this.chosenIndex === i;
      const w = this.cardWidth();
      const base = { width: `${w}px` };
      if (n <= 1) {
        return { ...base, transform: `translateX(-50%) translateY(${up ? -16 : 0}px) scale(${up ? 1.04 : 1})`, zIndex: 1 };
      }
      const middle = (n - 1) / 2;
      const off = i - middle;
      const step = Math.min(9, 34 / n);
      const spread = Math.min(w * 0.85, (FAN_WIDTH * this.fanScale * 1.4 - w) / (n - 1));
      const drop = Math.abs(off) * 5;
      const lift = up ? -18 : 0;
      return {
        ...base,
        transform: `translateX(-50%) translateX(${off * spread}px) translateY(${drop + lift}px) rotate(${off * step}deg) scale(${up ? 1.05 : 1})`,
        zIndex: up ? 50 : 10 + i,
      };
    },
    tapHand(i) {
      this.openSeat = null;
      this.chosenIndex = this.chosenIndex === i ? null : i;
    },
    // No card in hand: tap a seat to read who they are. Card in hand: tap a
    // seat to put the card on it.
    tapSeat(s) {
      if (!this.chosen) { this.openSeat = this.openSeat === s.id ? null : s.id; return; }
      // At dawn a picked-up card shows its prices for planning; it plays once the day starts.
      if (this.preview || s.state !== 'free' || !s.place?.check.ok) return;
      this.place(s.id);
    },
    place(counselor) {
      const card = this.chosen;
      if (!card) return;
      // A card that sends its workers to the Frontier (Call to Arms, or any
      // card the Captain likes) has nothing to share out: the engine sends
      // them all as orders, so asking where they go only to ignore the answer
      // was a lie (playtest s6cA4).
      const toBand = sendsToBand(this.civ, card.id, counselor);
      const givesWorkers = toBand ? 0 : cardGives(this.civ, card.id, counselor);
      // The Charter names its village when there is more than one to name.
      if (HALL_CARDS[card.id].charter && this.charterable.length > 1) {
        this.charterFrom = card.id;
        this.charterSeat = counselor;
        this.chosenIndex = null;
        return;
      }
      if (givesWorkers) {
        this.givingFrom = card.id;
        this.givingSeat = counselor;
        this.givingGives = givesWorkers;
        this.split = {};
        this.chosenIndex = null;
        return;
      }
      this.act('play', { card: card.id, counselor });
    },
    nudge(role, d) {
      const n = Math.max(0, (this.split[role] || 0) + d);
      if (d > 0 && !this.splitLeft) return;
      this.split = { ...this.split, [role]: n };
    },
    finishGiving() {
      this.act('play', { card: this.givingFrom, counselor: this.givingSeat, split: { ...this.split } });
    },
    finishCharter(village) {
      this.act('play', { card: this.charterFrom, counselor: this.charterSeat, village });
    },
    act(actionId, params = {}) {
      this.chosenIndex = null;
      this.openSeat = null;
      this.givingFrom = null;
      this.givingSeat = null;
      this.charterFrom = null;
      this.charterSeat = null;
      this.split = {};
      this.$emit('act', { role: 'culture', actionId, params });
    },
  },
};
</script>
